import { useContext } from 'react';
import { AuthContext, type AuthContextValue } from './auth-provider';
import type { AuthUser, UserRole } from './types';

/**
 * useAuth — the only way feature code reads auth state.
 *
 * Returns the raw `AuthContextValue` from the provider plus two convenience
 * getters:
 *
 *   - `user`: the current `AuthUser` (or `null` when unauthenticated)
 *   - `role`: the user's PRIMARY-org role, derived solely from the real
 *             session. This is a fallback: the effective, org-aware role that
 *             every RBAC gate reads comes from `useCurrentRole()`
 *             (`@shared/data/use-current-org`), which resolves the role for the
 *             actively-selected org (#318).
 *
 * There is deliberately no dev-role override in this path: a stale
 * `localStorage` value silently downgraded a real org_admin and was never
 * cleared on logout. Per CLAUDE.md rule 6, no dev toggles ship in shipped
 * routes — `role` derives only from the real session (#319).
 *
 * No component should import `AuthContext` directly. Keeps the swap to a real
 * provider a one-file edit (auth-provider.tsx) instead of a codebase grep.
 */
export type UseAuthResult = AuthContextValue & {
  user: AuthUser | null;
  role: UserRole | null;
};

export function useAuth(): UseAuthResult {
  const ctx = useContext(AuthContext);

  const user = ctx.session?.user ?? null;
  const role: UserRole | null = user?.role ?? null;

  return { ...ctx, user, role };
}
